import { type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon, ClipboardListIcon, cn } from '@quickmd-llc/design-system'
import { VIEW_INTAKE_FORM_ROUTE } from '@/utils/constants'
import type { IntakeFormListItem } from '@/utils/narrowing'
import { IF_COL } from '../intakeFormsColumns'

/*
 * SCSS → Tailwind mapping (IntakeFormsTableItem.scss deleted):
 *   .if-row:
 *     display:flex / align-items:center / gap:16px   → flex items-center gap-sp-2
 *     padding: 14px 16px                             → py-[14px] px-sp-2  (14px — no exact token)
 *     background: #ffffff                            → bg-white
 *     cursor: pointer                                → cursor-pointer
 *     border-bottom: 1px solid --border-card         → border-b border-shade-10
 *     :last-child border-bottom:none / rounded 0 0 8px 8px
 *                                                    → last:border-b-0 last:rounded-b-large
 *     :hover background teal tint                    → hover:bg-[#f2fafb]
 *       (rgba(0,162,178,0.05) ≈ #f2fafb — no exact DS token, same as StateSettingsRow/ServiceTypeRow)
 *   .if-form-icon:
 *     width/height:40px / border-radius:50% / bg:#eceff1 → w-sp-5 h-sp-5 rounded-full bg-deep-navy-10
 *     display:flex / align-items:center / justify-content:center / flex-shrink:0
 *                                                    → flex items-center justify-center shrink-0
 *     color: --text-dark                             → text-text-dark
 *   .if-name-block:
 *     display:flex / flex-direction:column / gap:2px → flex flex-col gap-sp-0.25
 *     min-width:0                                    → min-w-0
 *   .if-name:
 *     font-size:14px / font-weight:600 / line-height:20px → text-size-3 font-semibold leading-5
 *     letter-spacing:0.12px                          → tracking-[0.12px]
 *     color: --text-dark                             → text-text-dark
 *     white-space:nowrap / overflow:hidden / text-overflow:ellipsis → whitespace-nowrap truncate
 *   .if-service-pill:
 *     display:inline-flex / align-items:center / padding:2px 6px → inline-flex items-center py-sp-0.25 px-sp-0.75
 *     border-radius:100px                            → rounded-full
 *     background: #d8dee2                            → bg-deep-navy-20  (deepNavy[20] = #D8DEE2)
 *     font-size:12px / font-weight:600 / line-height:18px → text-size-2 font-semibold leading-[18px]
 *     letter-spacing:0.14px                          → tracking-[0.14px]
 *     color: --text-dark                             → text-text-dark
 *     white-space:nowrap / flex-shrink:0             → whitespace-nowrap shrink-0
 *   .if-responses:
 *     font-size:14px / font-weight:400 / line-height:20px → text-size-3 font-normal leading-5
 *     letter-spacing:0.12px                          → tracking-[0.12px]
 *     color: --text-medium                           → text-text-light
 *   .if-chevron:
 *     color: --text-medium                           → text-text-light
 */

type Props = {
  item: IntakeFormListItem
}

export const IntakeFormsTableItem = ({ item }: Props) => {
  const navigate = useNavigate()

  const handleItemClick = () => {
    navigate(`${VIEW_INTAKE_FORM_ROUTE}?id=${item.IntakeFormId}`)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleItemClick()
    }
  }

  return (
    <div
      className='flex cursor-pointer items-center gap-sp-2 border-b border-shade-10 bg-white px-sp-2 py-[14px] last:rounded-b-large last:border-b-0 hover:bg-[#f2fafb]'
      data-testid='pw_if-row'
      onClick={handleItemClick}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={0}>
      {/* Icon column */}
      <div className={cn(IF_COL.base, IF_COL.icon)}>
        <div className='flex h-sp-5 w-sp-5 shrink-0 items-center justify-center rounded-full bg-deep-navy-10 text-text-dark'>
          <ClipboardListIcon iconSize='m' />
        </div>
      </div>

      {/* Name column */}
      <div className={cn(IF_COL.base, IF_COL.name)}>
        <div className='flex min-w-0 flex-col gap-sp-0.25'>
          <span className='truncate whitespace-nowrap text-size-3 font-semibold leading-5 tracking-[0.12px] text-text-dark'>
            {item.Name}
          </span>
        </div>
      </div>

      {/* Arrow column */}
      <div className={cn(IF_COL.base, IF_COL.arrow)}>
        <ChevronRightIcon iconSize='s' className='text-text-light' />
      </div>
    </div>
  )
}
